import React, { useMemo } from "react";
import { geoMercator, geoPath } from "d3-geo";
import { useWorldFeatures } from "@/lib/useWorldFeatures";
import { ISO_NUMERIC, colorFor } from "@/lib/countryColors";
import { isArab, bboxOf, featureKey } from "@/lib/mapGeometry";

/** Projection + feature groups for the mini map; recomputed only when inputs change. */
function useMiniMapGeometry(features, code, size, position) {
  return useMemo(() => {
    if (!features) return null;
    const proj = geoMercator().center([21, 15]).scale(size * 0.75).translate([size / 2, size / 2]);
    const pathGen = geoPath(proj);
    const target = features.find((f) => f.properties.id === ISO_NUMERIC[code]) || null;
    return {
      pathGen,
      target,
      targetBbox: target ? bboxOf(pathGen, target) : null,
      targetPoint: position ? proj([position.lng, position.lat]) : null,
      otherArab: features.filter((f) => isArab(f) && f !== target),
      others: features.filter((f) => !isArab(f)),
    };
  }, [features, code, size, position]);
}

function FlagPattern({ id, flagUrl, bbox }) {
  return (
    <defs>
      <pattern id={id} patternUnits="userSpaceOnUse" x={bbox.x} y={bbox.y} width={bbox.w} height={bbox.h}>
        <image href={flagUrl} x={0} y={0} width={bbox.w} height={bbox.h} preserveAspectRatio="none" />
      </pattern>
    </defs>
  );
}

function FlagDot({ code, flagUrl, point }) {
  return (
    <g transform={`translate(${point[0]}, ${point[1]})`}>
      <defs>
        <clipPath id={`mm-clip-${code}`}><circle cx={0} cy={0} r={5} /></clipPath>
      </defs>
      <circle cx={0} cy={0} r={6} fill="#FFF" stroke="#2B2726" strokeWidth={0.6} />
      <image href={flagUrl} x={-5} y={-5} width={10} height={10} clipPath={`url(#mm-clip-${code})`} preserveAspectRatio="xMidYMid slice" />
    </g>
  );
}

/** Small map of the Arab world with the target country filled with its flag (CountryPanel header). */
export default function MiniMap({ code, flagUrl, position, size = 96 }) {
  const features = useWorldFeatures();
  const geo = useMiniMapGeometry(features, code, size, position);

  if (!geo) {
    return <div className="rounded-lg bg-sandDeep/40 border border-stoneLine" style={{ width: size, height: size }} />;
  }

  const { pathGen, target, targetBbox, targetPoint, otherArab, others } = geo;
  const patternId = `mm-flag-${code}`;
  const isTiny = !target || targetBbox.w * targetBbox.h < 25;

  return (
    <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} className="rounded-lg border border-stoneLine bg-[#E4EDF3]" data-testid={`mini-map-${code}`}>
      {flagUrl && targetBbox && <FlagPattern id={patternId} flagUrl={flagUrl} bbox={targetBbox} />}
      <g>
        {others.map((f) => <path key={featureKey("mm-nonarab", f)} d={pathGen(f)} fill="#E9E1D3" stroke="#CDBFA3" strokeWidth="0.15" />)}
      </g>
      <g opacity="0.4">
        {otherArab.map((f) => <path key={`mm-arab-${f.properties.id}`} d={pathGen(f)} fill="#D5C7B0" stroke="#FFF" strokeWidth="0.15" />)}
      </g>
      {target && <path d={pathGen(target)} fill={flagUrl ? `url(#${patternId})` : colorFor(code)} stroke="#2B2726" strokeWidth="0.6" />}
      {isTiny && targetPoint && flagUrl && <FlagDot code={code} flagUrl={flagUrl} point={targetPoint} />}
    </svg>
  );
}
